import { apiError } from '../apiError';
import api from '../axios.config';
import { ApiResponse } from 'types';

/**
 * AI voice-agent provider integrations, per organization.
 *
 * Points cpass at the runtime (voicex) a business's agents live on, with its own
 * credentials — so different businesses can use different servers/accounts. Same
 * shape as the telephony data-source integration. The secret is write-only: the
 * API never returns it, and a blank secret on update means "leave unchanged".
 */
export interface AiProvider {
  id?: string;
  name: string;
  providerType: 'VOICEX';
  baseUrl: string;
  /**
   * OAUTH2_CLIENT_CREDENTIALS is what voicex actually uses — it is an OAuth2
   * authorization server, so cpass exchanges a client id/secret for a bearer
   * token. The client id goes in authUsername, the secret in authSecret.
   */
  authType: 'NONE' | 'API_KEY' | 'BEARER' | 'BASIC' | 'OAUTH2_CLIENT_CREDENTIALS';
  authHeader?: string;
  authUsername?: string;
  authSecret?: string;
  /** Optional OAuth2 token endpoint; blank means derive {baseUrl}/v1/token. */
  tokenUrl?: string;
  /** Read-only: the token URL actually used, including the derived default. */
  effectiveTokenUrl?: string;
  /**
   * The business this org's agents belong to ON the provider — a voicex Mongo
   * ObjectId (24 hex). Without it agents are pushed unattributed and cannot be
   * listed back. Set by picking an existing business or creating one.
   */
  voicexBusinessId?: string;
  /**
   * Where an agent's media socket lives, e.g.
   * `wss://core-voicex.catchmycall.ai/chat/v2/{agent_id}`.
   *
   * NOT derivable from baseUrl — on the live runtime the socket is a different
   * host, scheme, port and path. Blank means agents get no socket URL, rather
   * than a guessed one that fails mid-call.
   */
  wsUrlTemplate?: string;
  /** Read-only: the template resolved with a stand-in id, to check its shape. */
  exampleWsUrl?: string;
  enabled?: boolean;
  connectTimeoutSeconds?: number;
  hasSecret?: boolean;
  lastTestedAt?: string;
  lastTestOk?: boolean;
  lastTestMessage?: string;
}

export interface ProviderTestResult { ok: boolean; elapsedMs?: number; message: string; hint?: string | null; }

/** A business as the provider knows it. `selected` = the one this provider is bound to. */
export interface ProviderBusiness { businessId: string; name?: string; selected?: boolean; }

const BASE = '/v1/ai-providers';
// Shared so a 403 reads as a refusal rather than a load failure.
const err = (e: any, msg: string) => apiError(e, msg);

export const aiProvidersService = {
  list: async (): Promise<ApiResponse<AiProvider[]>> => {
    try { return (await api.get(BASE)).data; } catch (e: any) { return err(e, 'Failed to load providers'); }
  },
  create: async (body: AiProvider): Promise<ApiResponse<AiProvider>> => {
    try { return (await api.post(BASE, body)).data; } catch (e: any) { return err(e, 'Failed to create provider'); }
  },
  update: async (id: string, body: AiProvider): Promise<ApiResponse<AiProvider>> => {
    try { return (await api.put(`${BASE}/${id}`, body)).data; } catch (e: any) { return err(e, 'Failed to update provider'); }
  },
  remove: async (id: string): Promise<ApiResponse<void>> => {
    try { return (await api.delete(`${BASE}/${id}`)).data; } catch (e: any) { return err(e, 'Failed to delete provider'); }
  },
  test: async (id: string): Promise<ApiResponse<ProviderTestResult>> => {
    try { return (await api.post(`${BASE}/${id}/test`)).data; } catch (e: any) { return err(e, 'Connection test failed'); }
  },

  /** Businesses on the provider, so the form can offer a picker instead of a raw ObjectId. */
  listBusinesses: async (id: string): Promise<ApiResponse<ProviderBusiness[]>> => {
    try { return (await api.get(`${BASE}/${id}/businesses`)).data; }
    catch (e: any) { return err(e, 'Failed to load businesses from the provider'); }
  },

  /**
   * Creates a business on the provider and binds this provider to it. The
   * provider mints the id. Refused if already bound — rebinding would orphan
   * every agent already pushed under the old id.
   */
  createBusiness: async (id: string, name: string, email?: string): Promise<ApiResponse<ProviderBusiness>> => {
    try { return (await api.post(`${BASE}/${id}/businesses`, { name, email })).data; }
    catch (e: any) { return err(e, 'Failed to create the business on the provider'); }
  },
};
